
import React, { useState, useEffect } from 'react';
import { Plant, UserProfile } from '../types';
import { storage, SmartActionResponse } from '../services/storage';
import { notifications } from '../services/notification';
import { 
  Droplets, Sprout, Loader2, Sparkles, Send, Calendar, CheckCircle2, 
  XCircle, Globe, BookOpen, Search, AlertCircle, BellOff, Bell, ExternalLink, Link2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SubscriptionStatusBadge } from '../components/SubscriptionStatusBadge';
import { t } from '../services/i18n';

export const Home: React.FC = () => {
  const [plants, setPlants] = useState<Plant[]>([]);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [smartInput, setSmartInput] = useState('');
  const [isProcessingAi, setIsProcessingAi] = useState(false);
  const [aiResult, setAiResult] = useState<SmartActionResponse | null>(null);
  const [notificationStatus, setNotificationStatus] = useState<NotificationPermission>(
    'Notification' in window ? Notification.permission : 'denied'
  );
  const navigate = useNavigate();

  const handleRequestNotifications = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationStatus(permission);
      if (permission === 'granted') {
        notifications.checkAndNotifyReminders();
      }
    }
  };

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        const [plantsData, userData] = await Promise.all([
          storage.getPlants(),
          storage.getUser()
        ]);
        if (isMounted) {
          setPlants(plantsData);
          setUser(userData);
          setError(false);
        }
      } catch (err) {
        console.error("Error fetching home data:", err);
        if (isMounted) setError(true);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchData();
    return () => { isMounted = false; };
  }, []);

  const handleSmartSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!smartInput.trim()) return;

    setIsProcessingAi(true);
    setAiResult(null); 
    try {
      const result = await storage.parseSmartCommand(smartInput);
      setAiResult(result);
      setSmartInput('');
    } catch (err) {
      console.error(err);
    } finally {
      setIsProcessingAi(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-raizama-action">
        <Loader2 className="animate-spin" size={32} />
        <span className="mt-4 text-[10px] text-raizama-textSec font-black uppercase tracking-[0.2em]">{t('home_loading')}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <SubscriptionStatusBadge />
      
      {/* Notification Banner */}
      {notificationStatus === 'default' && (
        <div className="bg-raizama-surface border border-raizama-border/30 rounded-2xl p-4 flex flex-col sm:flex-row items-center gap-4 justify-between shadow-lg">
          <div className="flex items-center gap-3 text-left w-full">
            <div className="p-2 bg-raizama-action/10 text-raizama-action rounded-lg shrink-0">
              <Bell size={24} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white mb-1">Ativar Notificações</h3>
              <p className="text-xs text-raizama-textSec">Seja avisado quando for a hora de regar e cuidar das suas plantas.</p>
            </div>
          </div>
          <button 
            onClick={handleRequestNotifications}
            className="w-full sm:w-auto shrink-0 bg-raizama-action text-white text-xs font-bold px-4 py-2 rounded-xl hover:bg-raizama-action/80 transition-colors whitespace-nowrap"
          >
            Permitir
          </button>
        </div>
      )}
      
      {notificationStatus === 'denied' && (
        <div className="bg-raizama-surface border border-raizama-border/30 rounded-2xl p-4 flex flex-col sm:flex-row items-center gap-4 justify-between shadow-lg opacity-70">
          <div className="flex items-center gap-3 text-left w-full">
            <div className="p-2 bg-red-500/10 text-red-400 rounded-lg shrink-0">
              <BellOff size={24} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white mb-1">Notificações Bloqueadas</h3>
              <p className="text-xs text-raizama-textSec">Você não receberá avisos sobre regas. Altere isso nas configurações do navegador.</p>
            </div>
          </div>
        </div>
      )}
      {/* Welcome Card */}
      <div className="bg-gradient-to-br from-raizama-action/30 via-raizama-surface to-raizama-bg p-6 rounded-3xl border border-raizama-action/20 shadow-2xl relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-raizama-action/10 rounded-full blur-3xl"></div>
        <h2 className="text-3xl font-black text-white mb-1 tracking-tight">
          {t('home_welcome')}, {user?.nome?.split(' ')[0] || t('home_gardener')}!
        </h2>
        <p className="text-raizama-textSec text-sm font-bold opacity-80">
          {t('home_slogan')}
        </p>
        
        <div className="flex gap-6 mt-6">
            <div className="flex flex-col">
                <span className="text-3xl font-black text-raizama-highlight">{plants.length}</span>
                <span className="text-[10px] text-raizama-textSec font-black uppercase tracking-widest">{t('home_specimens')}</span>
            </div>
            <div className="w-[1px] bg-raizama-border/20 h-10"></div>
            <div className="flex flex-col">
                <span className="text-3xl font-black text-yellow-500">
                  {plants.filter(p => p.status === 'Doente' || p.status === 'Observação').length}
                </span>
                <span className="text-[10px] text-raizama-textSec font-black uppercase tracking-widest">{t('home_alerts')}</span>
            </div>
        </div>
      </div>

      {/* Smart Assistant - Botanical Research Center */}
      <section className="bg-raizama-surface/50 p-5 rounded-3xl border border-raizama-border/10 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-raizama-action" />
            <h3 className="text-xs font-black text-raizama-textMain uppercase tracking-widest">{t('home_assistant')}</h3>
          </div>
          <div className="flex items-center gap-1.5 bg-raizama-action/10 px-2 py-1 rounded-full border border-raizama-action/20">
             <Globe size={10} className="text-raizama-action" />
             <span className="text-[8px] font-black text-raizama-action uppercase">Google Grounding</span>
          </div>
        </div>

        <form onSubmit={handleSmartSubmit} className="relative group">
          <input 
            type="text" 
            placeholder={t('home_assistant_placeholder')}
            value={smartInput}
            onChange={(e) => setSmartInput(e.target.value)}
            disabled={isProcessingAi}
            className="w-full bg-black/30 border border-raizama-border/20 rounded-2xl p-4 pr-12 text-sm text-white focus:outline-none focus:border-raizama-action transition-all placeholder:text-gray-600 disabled:opacity-50"
          />
          <button 
            type="submit" 
            disabled={isProcessingAi || !smartInput.trim()}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-raizama-action text-white rounded-xl shadow-lg hover:scale-105 active:scale-95 transition-all disabled:opacity-50 disabled:scale-100"
          >
            {isProcessingAi ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
          </button>
        </form>

        {aiResult && (
          <div className="mt-4 animate-scale-up">
            <div className="bg-raizama-surface border border-raizama-border/20 p-5 rounded-2xl shadow-inner overflow-hidden">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <BookOpen size={16} className="text-raizama-highlight" />
                    <span className="text-[10px] font-black text-raizama-highlight uppercase tracking-widest">
                      {aiResult.action === 'research' ? t('home_ai_report') : t('home_ai_action')}
                    </span>
                  </div>
                  <button onClick={() => setAiResult(null)} className="text-raizama-textSec hover:text-white"><XCircle size={16} /></button>
                </div>
                
                <div className="text-sm text-raizama-textSec leading-relaxed whitespace-pre-wrap max-h-96 overflow-y-auto pr-2 custom-scrollbar markdown-content mb-4">
                  {aiResult.message}
                </div>

                {/* Exibição das Fontes de Pesquisa (Grounding) */}
                {aiResult.sources && aiResult.sources.length > 0 && (
                  <div className="pt-4 border-t border-raizama-border/10">
                    <h4 className="text-[9px] font-black text-raizama-textSec uppercase tracking-[0.2em] mb-3 flex items-center gap-1.5">
                      <Link2 size={10} /> {t('home_ai_sources')}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {aiResult.sources.map((source, idx) => (
                        <a 
                          key={idx} 
                          href={source.uri} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 bg-black/20 hover:bg-raizama-action/10 px-2.5 py-1 rounded-md border border-raizama-border/5 text-[9px] text-raizama-textSec hover:text-raizama-action transition-all truncate max-w-[140px]"
                        >
                          <ExternalLink size={10} />
                          {source.title || 'Referência'}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        )}
      </section>

      {/* Daily Checklist Section */}
      <div>
        <h3 className="text-sm font-black text-raizama-textMain mb-4 flex items-center gap-2 uppercase tracking-widest px-1">
            <Droplets size={16} />
            {t('home_checklist')}
        </h3>
        
        <div className="space-y-3">
             {plants.length > 0 ? (
               plants.filter(p => p.reminders?.watering).length > 0 ? (
                 plants.filter(p => p.reminders?.watering).slice(0, 3).map(p => (
                   <div key={p.id} className="bg-raizama-surface p-4 rounded-2xl border border-raizama-border/10 flex items-center justify-between group hover:border-raizama-action/30 transition-all shadow-md">
                      <div className="flex items-center gap-4">
                          <div className="w-11 h-11 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-400">
                              <Droplets size={22} />
                          </div>
                          <div>
                              <p className="font-bold text-white text-sm">{p.nomeComum}</p>
                              <p className="text-[10px] text-raizama-textSec font-medium uppercase tracking-tighter">{p.planoManejo?.rega || t('home_check')}</p>
                          </div>
                      </div>
                      <button onClick={() => navigate(`/plant/${p.id}`)} className="p-2 text-raizama-textSec hover:text-white transition-colors">
                        <Calendar size={18} />
                      </button>
                   </div>
                 ))
               ) : (
                <div className="text-center py-6 bg-raizama-surface/20 rounded-2xl border border-dashed border-raizama-border/10">
                  <p className="text-[10px] text-raizama-textSec font-black uppercase">{t('home_all_done')}</p>
                </div>
               )
             ) : (
               <div className="text-center py-8 bg-raizama-surface/30 rounded-3xl border border-dashed border-raizama-border/20">
                 <p className="text-raizama-textSec text-xs font-bold uppercase tracking-widest">{t('home_empty')}</p>
               </div>
             )}
        </div>
      </div>
    </div>
  );
};